import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';

import getDefaultTopicUrl from 'utils/getDefaultTopicUrl';
import getDefaultArticleUrl from 'utils/getDefaultArticleUrl';
import formatAriaLabelWithTitle from 'utils/formatAriaLabelWithTitle';

import organizedTopic from 'state/selectors/organizedTopic';

import { ModuleHeader } from 'components/modules';
import { ImageCard, Pagination } from 'styled';

import {
  EmphasizeModuleColor,
  EmphasizeModuleColorScheme,
  GeneralModuleColorScheme,
  GeneralModuleColor,
} from 'constants/ColorScheme';

import {
  ThreeColumnInfoModule as IThreeColumnInfoModule,
  OrganizedTopic,
  ThreeColumnInfoModuleContent,
  GlobalState,
  Image,
} from 'types';

interface PassedProps {
  module: IThreeColumnInfoModule;
  hasWrapper?: boolean;
  wrapperColor?: EmphasizeModuleColor;
}

interface StoreProps {
  organizedTopic: OrganizedTopic;
}

type Props = PassedProps & StoreProps;

interface State {
  activeTab: string;
  prevActiveTab: string;
  paginationInfo: PaginationInfo;
}

interface PaginationInfo {
  [tab: string]: {
    currentPaginationNumber: number;
    totalPaginationCount: number;
    currentPageContents: ThreeColumnInfoModuleContent[];
  };
}

interface ThreeColumnInfoModuleSanitizedContent {
  type: 'article' | 'manualContent' | 'topic';
  id: string;
  title: string;
  image: Image;
  description: string;
  url: string;
  ariaLabel: string;
  buttonLabel: string;
  buttonAriaLabel: string;
  buttonUrl: string;
}

class ThreeColumnInfoModule extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const {
      module: { tabs },
    } = props;

    const selectedTabs = tabs.length > 0 ? tabs : ['default'];
    const paginationInfo = selectedTabs.reduce(
      (sanitizedPaginationInfo: PaginationInfo, tab: string) => {
        sanitizedPaginationInfo[tab] = {
          currentPaginationNumber: 1,
          totalPaginationCount: Math.ceil(
            props.module.contents[tab].length / props.module.maxItemsPerPage
          ),
          currentPageContents: this.getCurrentPageStories(tab, 1),
        };

        return sanitizedPaginationInfo;
      },
      {}
    );

    this.state = {
      activeTab: selectedTabs[0],
      prevActiveTab: '',
      paginationInfo,
    };
  }

  handleTabClick = (clickedTab: string) => {
    this.setState((state) => ({
      activeTab: clickedTab,
      prevActiveTab: state.activeTab,
    }));
  };

  getContent = (
    content: ThreeColumnInfoModuleContent
  ): ThreeColumnInfoModuleSanitizedContent | null => {
    if (!content) return null;

    if (content.type === 'manualContent') {
      return {
        type: 'manualContent',
        id: content.id,
        title: content.title,
        image: content.image,
        description: content.description,
        url: content.url,
        ariaLabel: content.ariaLabel,
        buttonLabel: content.button.label,
        buttonAriaLabel: content.button.ariaLabel,
        buttonUrl: content.button.url,
      };
    }

    const organizedTopic = this.props.organizedTopic;

    if (content.type === 'topic') {
      const url = getDefaultTopicUrl(organizedTopic, content.topic.slug);

      return {
        type: 'topic',
        id: content.id,
        title: content.topic.title,
        image: content.topic.moduleImage,
        description: '',
        url: url,
        ariaLabel: formatAriaLabelWithTitle(content.topic.title),
        buttonLabel: '',
        buttonAriaLabel: '',
        buttonUrl: '',
      };
    }

    if (content.type === 'article') {
      const url = getDefaultArticleUrl(content.article.slug);

      return {
        type: 'article',
        id: content.id,
        title: content.article.moduleTitle || content.article.title,
        image: content.article.moduleImage.id
          ? content.article.moduleImage
          : content.article.heroImage,
        description: content.article.moduleDescription,
        url: url,
        ariaLabel: formatAriaLabelWithTitle(
          content.article.moduleTitle || content.article.title
        ),
        buttonLabel: '',
        buttonAriaLabel: '',
        buttonUrl: '',
      };
    }

    return null;
  };

  getCurrentPageStories = (
    tab: string,
    currentPaginationNumber: number
  ): ThreeColumnInfoModuleContent[] => {
    const {
      module: { maxItemsPerPage, contents },
    } = this.props;

    return contents[tab].slice(
      currentPaginationNumber * maxItemsPerPage - maxItemsPerPage,
      currentPaginationNumber * maxItemsPerPage
    );
  };

  handlePageChange = (tab: string, pageNumber: number) => {
    this.setState((state: State) => ({
      paginationInfo: {
        ...state.paginationInfo,
        [tab]: {
          ...state.paginationInfo[tab],
          currentPaginationNumber: pageNumber + 1,
          currentPageContents: this.getCurrentPageStories(tab, pageNumber + 1),
        },
      },
    }));
  };

  render() {
    const {
      hasWrapper,
      wrapperColor,
      module,
      module: {
        button,
        contentAlign,
        contents,
        isTwoColumnOnMobile,
        moduleHeader,
        numberOptions,
        tabs,
        maxItemsPerPage,
      },
    } = this.props;

    if (!Object.keys(contents).length) return null;

    const { paginationInfo } = this.state;
    const { activeTab, prevActiveTab } = this.state;
    const contentKeys = tabs.length > 0 ? tabs : ['default'];
    const headerTitle = moduleHeader.title;
    const headerDescription = moduleHeader.description;
    const headerTabs = module.tabs;
    const contentAlignIsCenter = contentAlign === 'center';
    const wrapperColorScheme =
      hasWrapper && wrapperColor
        ? EmphasizeModuleColorScheme[wrapperColor]
        : null;
    const colorScheme =
      GeneralModuleColorScheme[
        module.moduleOptions.color as GeneralModuleColor
      ];
    const renderModuleHeader =
      headerTitle || headerDescription || !!headerTabs.length || button.label;

    return (
      <div
        className={cx('ThreeColumnInfoModule module-content-padding-x pb2_5', {
          [`bg-color-${colorScheme.background} color-${colorScheme.text}`]: !wrapperColorScheme,
          pt6: !renderModuleHeader,
        })}
      >
        {renderModuleHeader && (
          <ModuleHeader
            textIsCentered={contentAlignIsCenter}
            title={headerTitle}
            description={headerDescription}
            tabs={headerTabs}
            activeTab={activeTab}
            onTabClick={this.handleTabClick}
            buttonLabel={button.label}
            buttonUrl={button.url}
            buttonVariant={button.variant}
          />
        )}
        <div className="relative pb2 md:pb3_5">
          {contentKeys.map((key) => (
            <div
              className={cx('transition-short', {
                'absolute t0 l0 w100': key !== activeTab,
                'opacity-0 events-none hidden':
                  key !== activeTab && prevActiveTab,
                'transition-fade-out-short events-none':
                  key !== activeTab && !prevActiveTab,
              })}
              aria-hidden={key !== activeTab}
              key={key}
            >
              <div
                className={cx(
                  'ThreeColumnInfoModule__info-container transition-short flex flex-wrap flex-col sm:flex-row',
                  {
                    'ThreeColumnInfoModule__info-container--two-column': isTwoColumnOnMobile,
                  }
                )}
                key={key}
              >
                {paginationInfo[key].currentPageContents.map(
                  (item, i, contents) => {
                    const content = this.getContent(item);

                    if (!content) return null;

                    const {
                      title,
                      id,
                      image,
                      description,
                      url,
                      ariaLabel,
                      buttonLabel,
                      buttonAriaLabel,
                      buttonUrl,
                    } = content;

                    return (
                      <ImageCard
                        key={id}
                        contentType={content.type}
                        title={title}
                        description={description}
                        image={image}
                        buttonLabel={buttonLabel}
                        buttonAriaLabel={buttonAriaLabel}
                        buttonUrl={buttonUrl}
                        url={url}
                        ariaLabel={ariaLabel}
                        contentAlignIsCenter={contentAlignIsCenter}
                        numberOptions={{
                          ...numberOptions,
                          number: i + 1,
                        }}
                        totalNumberOfContents={contents.length}
                      />
                    );
                  }
                )}
              </div>
              {contents[key].length > maxItemsPerPage && (
                <div className="w100 flex justify-center mt3">
                  <Pagination
                    pageCount={paginationInfo[key].totalPaginationCount}
                    pageRangeDisplayed={5}
                    marginPagesDisplayed={0}
                    onPageChange={(currentPage: { selected: number }) =>
                      this.handlePageChange(key, currentPage.selected)
                    }
                    activeLinkBgColor="blue-darker"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState): StoreProps => ({
  organizedTopic: organizedTopic(state),
});

export default connect(mapStateToProps)(ThreeColumnInfoModule);
